import * as React from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import ModalLayout from "src/components/shared/atoms/modalLayout";
import ADD_PAYMENT_METHOD_FOR_CUSTOMERS from "src/graphql/mutation/settings/paymentMethod/addPaymentMethod";
import { useMutation } from "@apollo/client";
import { enqueueSnackbar } from "notistack";
import { ICurrentSubscriptionData } from "./types";
import useStyle from "./Styles";

interface ModalRefType {
  handleOpen: () => void;
  handleClose: () => void;
}

const cardElementStyle = {
  style: {
    base: {
      fontSize: "16px",
      color: "#32325d",
      fontSmoothing: "antialiased",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#fa755a" },
  },
};

interface Props {
  stripeCustomerId: string;
  setCurrentSubscriptionData?: React.Dispatch<
    React.SetStateAction<ICurrentSubscriptionData | undefined>
  >;
  openUpgradePlanModal?: any;
  setOpenPaymentMethodModal: any;
}

const AddNewMethodModal: React.FC<Props> = ({
  stripeCustomerId,
  setCurrentSubscriptionData,
  openUpgradePlanModal,
  //
  setOpenPaymentMethodModal,
}) => {
  const { classes } = useStyle();

  const modalRef = React.useRef<ModalRefType | null>(null);

  React.useEffect(() => {
    if (modalRef.current?.handleOpen) {
      setOpenPaymentMethodModal(() => modalRef.current!.handleOpen);
    }
  }, []);

  const handleClose = () => modalRef.current?.handleClose();
  const stripe: any = useStripe();
  const elements = useElements();

  const [cardError, setCardError] = React.useState("");
  const [expiryError, setExpiryError] = React.useState("");
  const [cvcError, setCvcError] = React.useState("");
  const [isFormValid, setIsFormValid] = React.useState(false);

  const validateForm = () => {
    const isValid = !cardError && !expiryError && !cvcError;
    setIsFormValid(isValid);
  };

  const handleCardChange = (event: any) => {
    setCardError(event.error ? event.error.message : "");
    validateForm();
  };

  const handleExpiryChange = (event: any) => {
    setExpiryError(event.error ? event.error.message : "");
    validateForm();
  };

  const handleCvcChange = (event: any) => {
    setCvcError(event.error ? event.error.message : "");
    validateForm();
  };

  const [
    addPaymentMethodForCustomers,
    {
      loading: addPaymentMethodForCustomersLoading,
      error: addPaymentMethodForCustomersError,
      data: addPaymentMethodForCustomersResult,
    },
  ] = useMutation(ADD_PAYMENT_METHOD_FOR_CUSTOMERS);

  React.useEffect(() => {
    if (addPaymentMethodForCustomersResult?.addPaymentMethodForCustomer) {
      // Extract the new payment method
      const newPaymentMethod =
        addPaymentMethodForCustomersResult?.addPaymentMethodForCustomer
          ?.paymentMethod;

      if (newPaymentMethod) {
        // it works in the billing screen for adding in the payment method in the realtime
        // Update the current subscription data with the new payment method
        setCurrentSubscriptionData?.((prevData: any) => ({
          ...prevData,
          paymentMethods: [
            ...(prevData?.paymentMethods || []),
            newPaymentMethod,
          ],
        }));
        // it opens the upgrde plan modal after adding the payment method in the upgrade plan
        // setOpenUpgradePopup?.(true);
        if (openUpgradePlanModal) openUpgradePlanModal();
      }
      //
      enqueueSnackbar(
        addPaymentMethodForCustomersResult.addPaymentMethodForCustomer.message,
        {
          variant: addPaymentMethodForCustomersResult
            ?.addPaymentMethodForCustomer?.success
            ? "success"
            : "error",
        },
      );
      handleClose();
    }
  }, [addPaymentMethodForCustomersResult]);

  React.useEffect(() => {
    if (addPaymentMethodForCustomersError) {
      enqueueSnackbar(addPaymentMethodForCustomersError.message, {
        variant: "error",
      });
    }
  }, [addPaymentMethodForCustomersError]);

  const handleAddPaymentMethod = async (e: any) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardNumberElement);

    // Create payment method
    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });

    if (error) {
      console.error(error.message);
    } else {
      await addPaymentMethodForCustomers({
        variables: {
          // eslint-disable-next-line object-shorthand
          stripeCustomerId: stripeCustomerId,
          paymentMethodId: paymentMethod.id,
        },
      });
    }
  };

  return (
    <ModalLayout
      headerText={
        <Typography className={classes.addPaymentMethodText}>
          Add Payment Method
        </Typography>
      }
      ref={modalRef}
      modalCustomStyle={{
        maxWidth: "500px",
        width: "100%",
      }}
    >
      <form onSubmit={handleAddPaymentMethod}>
        {/* Card Number */}
        <Typography variant="body2" className={classes.addDetailsFields}>
          Card Number
        </Typography>
        <Box
          sx={{
            border: "1px solid #ccc",
            borderRadius: "5px",
            padding: "10px",
            mb: 2,
          }}
        >
          <CardNumberElement
            options={cardElementStyle}
            onChange={handleCardChange}
          />
        </Box>
        {cardError && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {cardError}
          </Typography>
        )}

        {/* Expiration Date & CVC */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ width: "50%" }}>
            <Typography variant="body2" className={classes.addDetailsFields}>
              Expiration Date
            </Typography>
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "5px",
                padding: "10px",
              }}
            >
              <CardExpiryElement
                options={cardElementStyle}
                onChange={handleExpiryChange}
              />
            </Box>
            {expiryError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {expiryError}
              </Typography>
            )}
          </Box>

          <Box sx={{ width: "50%" }}>
            <Typography variant="body2" className={classes.addDetailsFields}>
              Security Code
            </Typography>
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "5px",
                padding: "10px",
              }}
            >
              <CardCvcElement
                options={cardElementStyle}
                onChange={handleCvcChange}
              />
            </Box>
            {cvcError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {cvcError}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Buttons */}
        <Box className={classes.bottomBtnBar}>
          <Button
            className={classes.cancelBtn}
            variant="contained"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            className={classes.saveBtn}
            type="submit"
            variant="contained"
            disabled={
              !stripe || !isFormValid || addPaymentMethodForCustomersLoading
            }
            endIcon={
              addPaymentMethodForCustomersLoading && (
                <CircularProgress color="primary" size={15} />
              )
            }
          >
            Add Payment Method
          </Button>
        </Box>
      </form>
    </ModalLayout>
  );
};

export default AddNewMethodModal;
